import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, FolderOpen, Link2, Target, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AionEmptyState } from "@/components/aion/AionEmptyState";
import { AionSectionCard } from "@/components/aion/AionSectionCard";

const STATUS_LABELS = {
  not_started: "Nicht gestartet",
  planned: "Geplant",
  in_progress: "In Arbeit",
  blocked: "Blockiert",
  at_risk: "Gefährdet",
  completed: "Abgeschlossen",
  cancelled: "Abgebrochen",
};

const PRIORITY_LABELS = {
  low: "Niedrig",
  medium: "Mittel",
  high: "Hoch",
  critical: "Kritisch",
};

export const projectProgress = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(0, Math.min(100, Math.round(numeric)));
};

const formatDate = (value) => {
  if (!value) return "Keine Deadline";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(parsed);
};

const projectSearchText = (project) => [
  project?.title,
  project?.summary,
  project?.status,
  project?.priority,
  project?.next_action,
  ...(project?.tags || []),
  ...(project?.blockers || []),
  ...(project?.risks || []),
  ...(project?.opportunities || []),
].filter(Boolean).join(" ").toLowerCase();

const projectReferences = (project) => {
  const refs = [];
  if (project?.goal_id) refs.push({ type: "Ziel", id: project.goal_id });
  (project?.decision_ids || []).forEach((id) => refs.push({ type: "Entscheidung", id }));
  (project?.relevant_memory_ids || []).forEach((id) => refs.push({ type: "Memory", id }));
  return refs;
};

export const ProjectsView = ({ activeProfile, projects = [], projectIntelligence = null, focusProjectId = null, onFocusConsumed, onOpenDecisions, onOpenMemory }) => {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const readOnlySharedProfile = activeProfile?.access_mode === "shared_view";
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const intelligenceProjects = projectIntelligence?.projects || [];
  const intelligenceSummary = projectIntelligence?.summary || {};

  const counts = useMemo(() => ({
    all: projects.length,
    active: projects.filter((item) => !["completed", "cancelled"].includes(item?.status)).length,
    completed: projects.filter((item) => item?.status === "completed").length,
    attention: projects.filter((item) => ["blocked", "at_risk"].includes(item?.status) || item?.priority === "critical").length,
  }), [projects]);

  const filteredProjects = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return projects.filter((project) => {
      if (statusFilter === "active" && ["completed", "cancelled"].includes(project?.status)) return false;
      if (statusFilter === "completed" && project?.status !== "completed") return false;
      if (statusFilter === "attention" && !(
        ["blocked", "at_risk"].includes(project?.status)
        || project?.priority === "critical"
      )) return false;
      return !needle || projectSearchText(project).includes(needle);
    });
  }, [projects, query, statusFilter]);

  useEffect(() => {
    if (!focusProjectId) return;
    if (projects.some((project) => project.id === focusProjectId)) {
      setStatusFilter("all");
      setQuery("");
      setSelectedProjectId(focusProjectId);
    }
    onFocusConsumed?.();
  }, [focusProjectId, onFocusConsumed, projects]);

  useEffect(() => {
    if (focusProjectId) return;
    if (!filteredProjects.length) {
      setSelectedProjectId(null);
      return;
    }
    if (!filteredProjects.some((project) => project.id === selectedProjectId)) {
      setSelectedProjectId(filteredProjects[0]?.id || null);
    }
  }, [filteredProjects, focusProjectId, selectedProjectId]);

  const selectedProject = useMemo(
    () => filteredProjects.find((project) => project.id === selectedProjectId) || null,
    [filteredProjects, selectedProjectId],
  );

  const refs = projectReferences(selectedProject);

  return (
    <div className="aion-view aion-view-stack" data-testid="projects-view">
      <AionSectionCard
        title="Projekte"
        eyebrow="PROJECT WORKSPACE"
        subtitle="Fortschritt, nächster Schritt, Risiken und verknüpfte Entscheidungen/Erinnerungen aus dem bestehenden AION Planning-System."
        testId="projects-hero-card"
      >
        <div className="aion-status-badge" data-testid="projects-profile-badge">
          <span>Profil: {activeProfile?.name || "Ich"}</span>
          <small>{activeProfile?.access_mode === "shared_view" ? "Freigegebene Projekte" : "Privater Projektbereich"}</small>
        </div>
        <div className="aion-inline-stats" data-testid="project-counts">
          <div className="aion-inline-stat"><span>Gesamt</span><strong>{counts.all}</strong></div>
          <div className="aion-inline-stat"><span>Aktiv</span><strong>{counts.active}</strong></div>
          <div className="aion-inline-stat"><span>Abgeschlossen</span><strong>{counts.completed}</strong></div>
          <div className="aion-inline-stat"><span>Aufmerksamkeit</span><strong>{counts.attention}</strong></div>
        </div>
        <div className="aion-search-row" data-testid="project-search-row">
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Projekte durchsuchen"
            data-testid="project-search-input"
          />
          <div className="aion-inline-actions" data-testid="project-filter-actions">
            {[
              ["active", "Aktiv"],
              ["all", "Alle"],
              ["completed", "Fertig"],
              ["attention", "Achtung"],
            ].map(([id, label]) => (
              <button
                type="button"
                key={id}
                className={`aion-section-nav-item${statusFilter === id ? " aion-section-nav-item-active" : ""}`}
                onClick={() => setStatusFilter(id)}
                data-testid={`project-filter-${id}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </AionSectionCard>

      <AionSectionCard
        title="Unternehmensanalyse"
        eyebrow="MULTI-PROJECT INTELLIGENCE"
        subtitle="Read-only Analyse der verbundenen Systeme. Projekte bleiben technisch getrennt."
        testId="project-intelligence-card"
      >
        <div className="aion-inline-stats" data-testid="project-intelligence-summary">
          <div className="aion-inline-stat"><span>Projekte</span><strong>{intelligenceSummary.total_projects ?? intelligenceProjects.length}</strong></div>
          <div className="aion-inline-stat"><span>Verbunden</span><strong>{intelligenceSummary.configured_projects ?? 0}</strong></div>
          <div className="aion-inline-stat"><span>Achtung</span><strong>{intelligenceSummary.attention_projects ?? 0}</strong></div>
          <div className="aion-inline-stat"><span>Offene Aufgaben</span><strong>{intelligenceSummary.open_tasks ?? 0}</strong></div>
        </div>
        {intelligenceProjects.length ? (
          <div className="aion-memory-list" data-testid="project-intelligence-list">
            {intelligenceProjects.map((project, index) => {
              const needsAttention = ["attention", "critical"].includes(project?.health)
                || ["degraded", "disconnected"].includes(project?.connector_state)
                || (project?.alerts || []).length > 0
                || (project?.blockers || []).length > 0;
              return (
                <div className="aion-memory-card" key={project.key || index} data-testid={`project-intelligence-item-${index}`}>
                  <strong>{project.name || project.key}</strong>
                  <small>
                    {project.connector_state === "connected" ? "Verbunden" : project.connector_state === "not_configured" ? "Noch nicht verbunden" : project.connector_state || "Unbekannt"}
                    {" · "}
                    {project.health === "healthy" ? "Gesund" : project.health === "critical" ? "Kritisch" : project.health === "attention" ? "Achtung" : "Unbekannt"}
                    {project.stale ? " · Daten veraltet" : ""}
                  </small>
                  {(project.metrics || []).length ? (
                    <small>{project.metrics.slice(0, 4).map((metric) => `${metric.label}: ${metric.value ?? "—"}${metric.unit ? ` ${metric.unit}` : ""}`).join(" · ")}</small>
                  ) : (
                    <small>Noch keine Live-KPIs verfügbar.</small>
                  )}
                  {needsAttention ? (
                    <div className="aion-status-badge">
                      <AlertTriangle className="aion-list-icon" aria-hidden="true" />
                      <span>{[...(project.alerts || []), ...(project.blockers || []), ...(project.risks || [])][0] || "Projekt benötigt Aufmerksamkeit."}</span>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <AionEmptyState
            icon={FolderOpen}
            title="Noch keine Projektanalyse verfügbar."
            description="Sobald ein Projekt-Connector Daten liefert, erscheinen Health, KPIs und Warnungen hier."
            testId="project-intelligence-empty"
          />
        )}
      </AionSectionCard>

      <div className="aion-two-column" data-testid="projects-workspace">
        <AionSectionCard title="Projektliste" eyebrow="PLANNING" subtitle="Wähle ein Projekt für Details und Referenzen." testId="project-list-card">
          {filteredProjects.length ? (
            <div className="aion-memory-list" data-testid="project-list">
              {filteredProjects.map((project, index) => {
                const active = project.id === selectedProjectId;
                return (
                  <button
                    type="button"
                    key={project.id || index}
                    className={`aion-memory-card${active ? " aion-memory-card-active" : ""}`}
                    aria-current={active ? "true" : undefined}
                    onClick={() => setSelectedProjectId(project.id)}
                    data-testid={`project-item-${index}`}
                  >
                    <strong>{project.title || `Projekt ${index + 1}`}</strong>
                    <small>
                      {STATUS_LABELS[project.status] || project.status || "Geplant"} · {projectProgress(project.progress)}% · {PRIORITY_LABELS[project.priority] || project.priority || "Mittel"}
                    </small>
                    <small>{project.next_action || "Noch kein nächster Schritt hinterlegt."}</small>
                  </button>
                );
              })}
            </div>
          ) : (
            <AionEmptyState
              icon={FolderOpen}
              title="Keine passenden Projekte."
              description="Sobald Projekte im Planning-System vorliegen, erscheinen sie hier."
              testId="projects-empty"
            />
          )}
        </AionSectionCard>

        <AionSectionCard
          title={selectedProject?.title || "Projekt öffnen"}
          eyebrow="DETAILS"
          subtitle={selectedProject ? `${STATUS_LABELS[selectedProject.status] || selectedProject.status || "Geplant"} · ${PRIORITY_LABELS[selectedProject.priority] || selectedProject.priority || "Mittel"}` : "Wähle links ein Projekt aus."}
          testId="project-detail-card"
        >
          {selectedProject ? (
            <div className="aion-view-stack" data-testid="project-detail">
              <div className="aion-inline-stats">
                <div className="aion-inline-stat"><span>Fortschritt</span><strong>{projectProgress(selectedProject.progress)}%</strong></div>
                <div className="aion-inline-stat"><span>Deadline</span><strong>{formatDate(selectedProject.deadline)}</strong></div>
                <div className="aion-inline-stat"><span>Sichtbarkeit</span><strong>{selectedProject.visibility || "private"}</strong></div>
              </div>

              <div className="aion-note" data-testid="project-summary">
                <strong>Zusammenfassung</strong>
                <p>{selectedProject.summary || "Keine Projektzusammenfassung hinterlegt."}</p>
              </div>

              <div className="aion-note" data-testid="project-next-action">
                <strong><Zap className="aion-button-icon" aria-hidden="true" /> Nächster Schritt</strong>
                <p>{selectedProject.next_action || "Noch kein nächster Schritt hinterlegt."}</p>
              </div>

              {(selectedProject.blockers || []).length ? (
                <div className="aion-list" data-testid="project-blockers">
                  {(selectedProject.blockers || []).map((item, index) => (
                    <div className="aion-list-row" key={`blocker-${index}`}>
                      <AlertTriangle className="aion-list-icon" aria-hidden="true" />
                      <div><strong>Blocker</strong><small>{item}</small></div>
                    </div>
                  ))}
                </div>
              ) : null}

              {(selectedProject.risks || []).length ? (
                <div className="aion-list" data-testid="project-risks">
                  {(selectedProject.risks || []).map((item, index) => (
                    <div className="aion-list-row" key={`risk-${index}`}>
                      <AlertTriangle className="aion-list-icon" aria-hidden="true" />
                      <div><strong>Risiko</strong><small>{item}</small></div>
                    </div>
                  ))}
                </div>
              ) : null}

              {(selectedProject.opportunities || []).length ? (
                <div className="aion-list" data-testid="project-opportunities">
                  {(selectedProject.opportunities || []).map((item, index) => (
                    <div className="aion-list-row" key={`opportunity-${index}`}>
                      <CheckCircle2 className="aion-list-icon" aria-hidden="true" />
                      <div><strong>Chance</strong><small>{item}</small></div>
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="aion-note" data-testid="project-references">
                <div className="aion-list-row-actions">
                  <strong><Link2 className="aion-button-icon" aria-hidden="true" /> Referenzen</strong>
                  <div className="aion-inline-actions">
                    {(selectedProject.decision_ids || []).length ? (
                      <Button size="sm" variant="outline" onClick={() => onOpenDecisions?.(selectedProject.decision_ids[0])} data-testid="project-open-decisions">Entscheidung öffnen</Button>
                    ) : null}
                    {!readOnlySharedProfile && onOpenMemory ? <Button size="sm" variant="outline" onClick={() => onOpenMemory()} data-testid="project-open-memory">Memory öffnen</Button> : null}
                  </div>
                </div>
                {refs.length ? (
                  <div className="aion-memory-reference-list">
                    {refs.map((reference) => (
                      <span key={`${reference.type}-${reference.id}`}>
                        {readOnlySharedProfile ? reference.type : `${reference.type}: ${reference.id}`}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p>Noch keine Decision-, Goal- oder Memory-Referenzen verknüpft.</p>
                )}
              </div>

              {(selectedProject.tags || []).length ? (
                <div className="aion-status-badge" data-testid="project-tags">
                  <Target className="aion-list-icon" aria-hidden="true" />
                  <span>{selectedProject.tags.map((tag) => `#${tag}`).join(" · ")}</span>
                </div>
              ) : null}
            </div>
          ) : (
            <AionEmptyState icon={FolderOpen} title="Wähle ein Projekt aus." description="Projektstatus, nächster Schritt und Referenzen erscheinen hier." testId="project-detail-empty" />
          )}
        </AionSectionCard>
      </div>
    </div>
  );
};

export { projectReferences };
